import { CollectionViewer, DataSource } from '@angular/cdk/collections';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { UserService } from '@app/shared/services/user/user.service';
import { catchError, finalize, map } from 'rxjs/operators';
import { ManagingCompanyService } from '@app/shared/services/managing-company.service';
import { AuthMethod } from '@app/shared/models/auth-method';
import { ManagingCompany } from '@app/shared/models/building.interface';
import { GlobalAuthority } from '@app/shared/models/global-authority';
import { BuildingAuthorization } from '@app/shared/models/building-authorization.interface';
import { User } from '@app/shared/models/user.interface';
import { UserManagingCompanyAuthorities } from '@app/shared/models/managing-company-authorities.interface';

export class UserDatasource implements DataSource<UserOutline> {
  private readonly usersSubject = new BehaviorSubject<UserOutline[]>([]);
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly countSubject = new BehaviorSubject<number>(0);
  public loading$ = this.loadingSubject.asObservable();
  public counter$ = this.countSubject.asObservable();

  constructor(
    private readonly usersService: UserService,
    private readonly managingCompanyService: ManagingCompanyService,
    private readonly usersLoaded?: () => void
  ) {}

  loadUsers(filter: string, sortDirection: string, pageIndex: number, pageSize: number): void {
    this.loadingSubject.next(true);
    combineLatest([
      this.managingCompanyService.getManagingCompanies(),
      this.usersService.findUsers(filter, sortDirection, pageIndex, pageSize)
    ])
      .pipe(
        map(([managingCompanies, users]) => {
          users.content = users.content.map((user: User) => {
            const userManagingCompanies = managingCompanies.filter(mc => user.managingCompanyIds?.includes(mc.id));
            return UserOutline.fromUser(user, userManagingCompanies);
          });
          return users;
        }),
        catchError(() => of([])),
        finalize(() => this.loadingSubject.next(false))
      )
      .subscribe((users) => {
        this.usersSubject.next(users.content);
        this.countSubject.next(users.totalElements);
        this.usersLoaded?.();
      });
  }

  connect(collectionViewer: CollectionViewer): Observable<UserOutline[]> {
    return this.usersSubject.asObservable();
  }

  disconnect(collectionViewer: CollectionViewer): void {
    this.usersSubject.complete();
    this.loadingSubject.complete();
  }
}

export interface IUserOutline {
  id: number;
  name: string;
  pictureUrl: string;
  emailAddress: string;
  authKey: string;
  authMethod: AuthMethod;
  managingCompany?: ManagingCompany;
  globalAuthorities: GlobalAuthority[];
  buildingAuthorizations: BuildingAuthorization[];
  managingCompanyAuthorities: UserManagingCompanyAuthorities[];
  headline: string;
  joinedAt: string;
  tenantsMap: {};
  authSource: string;
  lastLoggedIn: Date;
}

export class UserOutline implements IUserOutline {
  constructor(
    public id: number,
    public name: string,
    public pictureUrl: string,
    public emailAddress: string,
    public authKey: string,
    public authMethod: AuthMethod,
    public globalAuthorities: GlobalAuthority[],
    public buildingAuthorizations: BuildingAuthorization[],
    public managingCompanyAuthorities: UserManagingCompanyAuthorities[],
    public headline: string,
    public joinedAt: string,
    public tenantsMap: {},
    public authSource: string,
    public lastLoggedIn: Date,
    public managingCompanies?: ManagingCompany[]
  ) {}

  public static fromUser(user: User, managingCompanies?: ManagingCompany[]): UserOutline {
    return new UserOutline(
      user.id,
      user.name,
      user.pictureUrl,
      user.emailAddress,
      user.authKey,
      user.authMethod,
      user.globalAuthorities,
      user.buildingAuthorizations,
      user.managingCompanyAuthorities ?? [],
      user.headline,
      user.joinedAt,
      user.tenantsMap,
      user.authSource,
      user.lastLoggedIn ? new Date(user.lastLoggedIn) : null,
      managingCompanies || []
    );
  }

  public toUser(): User {
    return new User(
      this.id,
      this.name,
      this.pictureUrl,
      this.emailAddress,
      this.authKey,
      this.authMethod,
      this.managingCompanies?.map(mc => mc.id) || [],
      this.globalAuthorities,
      this.buildingAuthorizations,
      this.managingCompanyAuthorities,
      this.headline,
      this.joinedAt,
      this.tenantsMap,
      this.authSource,
      this.lastLoggedIn
    );
  }
}
